import React, { useCallback, useMemo, useState } from 'react';
import { ActionIcon, Box, Button, Group, Paper, Text, Transition } from '@mantine/core';
import { IconMapPin, IconX } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '../../navigation/appRoutes';
import type { LocationTreeNode } from '../../api/schedulingApis';
import type { TaskSnapshot } from '../../stores/types';
import {
  deriveScheduleReadiness,
  type ScheduleReadinessResult,
  type ScheduleReadinessState,
} from './deriveScheduleReadiness';
import {
  dismissScheduleReadiness,
  shouldShowScheduleReadiness,
} from './scheduleReadinessPreference';

export type ScheduleReadinessSurface = 'gantt' | 'locations';

type BannerCopy = {
  title: string;
  body: string;
  primaryLabel: string;
  primaryHref?: string;
  secondaryLabel?: string;
  secondaryHref?: string;
};

function copyForState(
  state: ScheduleReadinessState,
  surface: ScheduleReadinessSurface,
  readiness: ScheduleReadinessResult,
): BannerCopy | null {
  if (state === 'ready') return null;

  if (state === 'needs_tasks') {
    if (surface === 'locations') {
      return {
        title: 'Schedule readiness — locations first',
        body: 'You can build the site hierarchy now without tasks. Add or import work later when you are ready to assign locations to tasks.',
        // Same as needs_locations: stay on this page; no CTA that pulls users to Tasks first.
        primaryLabel: 'Add locations below',
      };
    }
    return {
      title: 'Schedule readiness — start your schedule',
      body: 'Add a root task to begin, or import an existing plan if you already have a file.',
      primaryLabel: 'Add root task',
      secondaryLabel: 'Import a plan',
      secondaryHref: APP_ROUTES.schedule.import,
    };
  }

  if (state === 'needs_locations') {
    if (surface === 'locations') {
      return {
        title: 'Schedule readiness — build your site structure',
        body: 'Add blocks, floors, or other locations under the project. Tasks need a real site hierarchy before you can assign them.',
        primaryLabel: 'Add locations below',
      };
    }
    return {
      title: 'Schedule readiness — build locations',
      body: 'Your schedule has tasks, but the location tree is empty or project-only. Build locations so work can be placed on site.',
      primaryLabel: 'Open Location management',
      primaryHref: APP_ROUTES.schedule.locations,
    };
  }

  // needs_assignment
  const unassigned = readiness.unassignedLeafCount;
  const leaf = readiness.leafTaskCount;
  if (surface === 'locations') {
    return {
      title: 'Schedule readiness — assign locations on tasks',
      body: `${unassigned} of ${leaf} leaf task${leaf === 1 ? '' : 's'} still need a location. Next: open Tasks and set Location name, or use Reassign locations.`,
      primaryLabel: 'Go to Tasks',
      primaryHref: APP_ROUTES.schedule.tasks,
    };
  }
  return {
    title: 'Schedule readiness — assign locations',
    body: `${unassigned} of ${leaf} leaf task${leaf === 1 ? '' : 's'} lack a location. Reassign from the hierarchy, set Location name on the grid, or edit locations.`,
    primaryLabel: 'Reassign locations',
    secondaryLabel: 'Open Location management',
    secondaryHref: APP_ROUTES.schedule.locations,
  };
}

export type ScheduleReadinessBannerProps = {
  projectId: string;
  tasks: TaskSnapshot[];
  locationTree: LocationTreeNode[] | null | undefined;
  surface: ScheduleReadinessSurface;
  /** When needs_tasks on Gantt — parent opens add-root-task modal. */
  onAddRootTask?: () => void;
  /** When needs_assignment on Gantt — same as Tools → Tasks → Reassign locations. */
  onReassignLocations?: () => void;
  reassignLocationsLoading?: boolean;
  /** Called when popover becomes visible for tip triggers (not on temporary dismiss). */
  onVisibleState?: (state: ScheduleReadinessState, readiness: ScheduleReadinessResult) => void;
  /** Hide while workflow / gate modals are open (popover returns when cleared). */
  suppressed?: boolean;
  /**
   * When false, hide the popover until schedule data is ready and the Gantt can
   * show tasks (avoids CTAs before load / false needs_* flash).
   * Defaults to true.
   */
  dataReady?: boolean;
};

/** Fixed bottom-right readiness popover (firm until ready; temporary session dismiss). */
export function ScheduleReadinessBanner({
  projectId,
  tasks,
  locationTree,
  surface,
  onAddRootTask,
  onReassignLocations,
  reassignLocationsLoading = false,
  onVisibleState,
  suppressed = false,
  dataReady = true,
}: ScheduleReadinessBannerProps) {
  const navigate = useNavigate();
  const readiness = useMemo(
    () => deriveScheduleReadiness(tasks, locationTree),
    [tasks, locationTree],
  );
  const [dismissTick, setDismissTick] = useState(0);

  const eligible = useMemo(() => {
    void dismissTick;
    return shouldShowScheduleReadiness(projectId, readiness.state);
  }, [projectId, readiness.state, dismissTick]);

  const showPopover =
    dataReady && eligible && !suppressed && readiness.state !== 'ready';
  const copy = showPopover ? copyForState(readiness.state, surface, readiness) : null;

  React.useEffect(() => {
    if (showPopover) {
      onVisibleState?.(readiness.state, readiness);
    }
  }, [showPopover, readiness, onVisibleState]);

  const handleDismiss = useCallback(() => {
    dismissScheduleReadiness(projectId, readiness.state);
    setDismissTick((n) => n + 1);
  }, [projectId, readiness.state]);

  const runPrimary = () => {
    if (!copy) return;
    if (readiness.state === 'needs_tasks' && surface === 'gantt' && onAddRootTask) {
      onAddRootTask();
      return;
    }
    if (
      readiness.state === 'needs_assignment' &&
      surface === 'gantt' &&
      onReassignLocations
    ) {
      onReassignLocations();
      return;
    }
    if (copy.primaryHref) navigate(copy.primaryHref);
  };

  const showPrimary =
    !!copy &&
    copy.primaryLabel !== 'Add locations below' &&
    (!!copy.primaryHref ||
      (readiness.state === 'needs_tasks' && surface === 'gantt' && !!onAddRootTask) ||
      (readiness.state === 'needs_assignment' &&
        surface === 'gantt' &&
        !!onReassignLocations));

  return (
    <Transition mounted={!!copy} transition="slide-up" duration={200} timingFunction="ease">
      {(styles) => (
        <Paper
          role="dialog"
          aria-label={copy?.title ?? 'Schedule readiness'}
          shadow="lg"
          radius="md"
          p="md"
          withBorder
          style={{
            ...styles,
            position: 'fixed',
            right: 20,
            bottom: 20,
            zIndex: 350,
            width: 'min(380px, calc(100vw - 32px))',
            maxWidth: 380,
            boxShadow:
              '0 12px 40px rgba(15, 23, 42, 0.16), 0 2px 8px rgba(15, 23, 42, 0.08)',
          }}
        >
          <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs" mb={8}>
            <Group gap={8} wrap="nowrap" align="flex-start" style={{ minWidth: 0, flex: 1 }}>
              <Box
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: 'var(--mantine-color-blue-light, #e7f5ff)',
                  color: 'var(--mantine-color-blue-7, #1c7ed6)',
                }}
                aria-hidden
              >
                <IconMapPin size={15} stroke={1.75} />
              </Box>
              <Text fw={600} size="sm" style={{ lineHeight: 1.35, minWidth: 0 }}>
                {copy?.title}
              </Text>
            </Group>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label="Dismiss for now"
              onClick={handleDismiss}
              disabled={reassignLocationsLoading}
            >
              <IconX size={14} />
            </ActionIcon>
          </Group>

          <Text size="sm" c="dimmed" mb="md" style={{ lineHeight: 1.45 }}>
            {copy?.body}
          </Text>

          <Group gap="xs" justify="flex-end">
            {copy?.secondaryHref ? (
              <Button
                size="xs"
                variant="default"
                disabled={reassignLocationsLoading}
                onClick={() => navigate(copy.secondaryHref!)}
              >
                {copy.secondaryLabel}
              </Button>
            ) : null}
            {showPrimary ? (
              <Button
                size="xs"
                loading={
                  readiness.state === 'needs_assignment' &&
                  surface === 'gantt' &&
                  reassignLocationsLoading
                }
                onClick={runPrimary}
              >
                {copy!.primaryLabel}
              </Button>
            ) : null}
          </Group>
        </Paper>
      )}
    </Transition>
  );
}

/** Quiet one-line why copy for tips / docs — Handoff mentioned once. */
export const SCHEDULE_LOCATIONS_WHY =
  'Locations tell the schedule where work happens so site reporting in Handoff stays accurate.';
